import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Form } from "antd";
import { useParams } from "react-router-dom";
import apiClient from "services/apiClient";

import { useMyContext } from "Context/MyContextProvider";
import ChatContentFooter from "./ConversationComps/ChatContentFooter";
import ChatContentBody from "./ConversationComps/ChatContentBody";
import ChatContentHeader from "./ConversationComps/ChatContentHeader";
import { useLiveChatContext } from "./LiveChatContext";
import { useChatMessageSearch } from "./useChatMessageSearch";
import {
  appendUniqueMessage,
  findChatByNumber,
  resolveCswStateFromChat,
  resolveCswStateFromPayload,
} from "./chatUtils";
import useSelectedMedia from "./useSelectedMedia";
import useMessageSender from "./useMessageSender";

// Re-exported for backward compatibility — the canonical source is now chatUtils.js
export { getInitials, getColorForName } from "./chatUtils";

const Conversation = (props) => {
  const { menuState } = props;
  const {
    api,
    UserData,
    authToken,
    isSupportAgent,
    isMobile,
  } = useMyContext();
  const {
    msgList,
    fetchMessages,
    appendOptimistic,
    updateMessagesForContact,
    setActiveContact,
    loadingMessages,
    setLoadingMessages,
    chatList,
    openContactCsw,
    chatAccount,
  } = useLiveChatContext();
  const params = useParams();
  const { id } = params;

  const [info, setInfo] = useState({});
  const [keywords, setKeywords] = useState([]);
  const [activeAgent, setActiveAgent] = useState();
  const [lastReportID, setLastReportID] = useState();
  const [isOldChat, setIsOldChat] = useState(false);
  const [cswExpiresAt, setCswExpiresAt] = useState(null);

  // Immediately populate header details from cached chat list if available to avoid blank avatars
  useEffect(() => {
    if (!info?.name && chatList?.length > 0 && id) {
      const cachedChat = chatList.find((c) => String(c.number) === String(id));
      if (cachedChat && cachedChat.name) {
        setInfo((prev) => ({
          ...prev,
          name: cachedChat.name,
          number: cachedChat.number,
        }));
      }
    }
  }, [id, chatList, info?.name]);

  const [form] = Form.useForm();
  const lastFetchedNumberRef = useRef(null);
  const chatBodyRef = useRef(null);
  const setHasMoreRef = useRef(null);

  const messageSearch = useChatMessageSearch({
    api,
    contactId: id,
    messages: msgList,
    updateMessagesForContact,
    scrollToMessageRef: chatBodyRef,
  });

  const parentNumber = useMemo(
    () => chatAccount?.whatsapp_number ?? UserData?.whatsapp_number ?? "",
    [chatAccount?.whatsapp_number, UserData?.whatsapp_number]
  );

  const scrollToBottom = useCallback((behavior = "smooth") => {
    chatBodyRef.current?.scrollToBottom?.(behavior);
  }, []);

  // ─── Selected media hook ────────────────────────────────────────────────────
  const {
    selectedMedia,
    disable,
    setDisable,
    updateSendDisabled,
    addSelectedMedia,
    beginDeviceMediaUploads,
    completeDeviceMediaUpload,
    failDeviceMediaUpload,
    removeSelectedMedia,
    clearSelectedMedia,
  } = useSelectedMedia({ form, id, chatAccount, UserData });

  // ─── Message sender hook ────────────────────────────────────────────────────
  const {
    replyingToPreview,
    handleReply,
    clearReply,
    onSend,
    sendLocationMessage,
  } = useMessageSender({
    id,
    api,
    authToken,
    lastReportID,
    infoNumber: info?.number,
    updateMessagesForContact,
    appendOptimistic,
    clearSelectedMedia,
    form,
    selectedMedia,
    setDisable,
    scrollToBottom,
  });

  // ─── Conversation metadata ─────────────────────────────────────────────────

  const handleAssignSuccess = useCallback((payload) => {
    if (payload?.active_agent) {
      setActiveAgent(payload.active_agent);
    }
    if (payload?.system_message) {
      updateMessagesForContact(id, (prev) => appendUniqueMessage(prev, payload.system_message));
    }
  }, [id, updateMessagesForContact]);

  const applyConversationMetadata = useCallback((chats) => {
    if (!chats) {
      return;
    }

    setInfo(chats);
    setActiveAgent(chats?.active_agent);

    const lastItem = chats?.msg
      ?.filter((item) => item.from === "opposite")
      .pop();
    setLastReportID(lastItem && lastItem.id);

    const cswState = resolveCswStateFromPayload(chats, chatList, id);
    if (cswState) {
      setIsOldChat(cswState.is_expired);
      setCswExpiresAt(cswState.csw_expires_at);
    }
  }, [chatList, id]);

  const applyCswFromChatList = useCallback((contactId) => {
    const cswState = resolveCswStateFromChat(findChatByNumber(chatList, contactId));
    if (!cswState) {
      return;
    }

    setIsOldChat(cswState.is_expired);
    setCswExpiresAt(cswState.csw_expires_at);
  }, [chatList]);

  const getChats = useCallback(
    async (number, handleLoader = true, page = 1, append = false, setHasMore) => {
      const numberChanged =
        String(lastFetchedNumberRef.current) !== String(number);

      if (numberChanged) {
        setActiveAgent(null);
        setLastReportID(null);
        setInfo({});
        applyCswFromChatList(number);
        lastFetchedNumberRef.current = String(number);
      }

      const chats = await fetchMessages(
        number,
        handleLoader,
        page,
        append,
        setHasMore || setHasMoreRef.current
      );

      if (!chats) {
        return;
      }

      if (numberChanged || page === 1 || !append) {
        applyConversationMetadata(chats);
      }
    },
    [applyConversationMetadata, applyCswFromChatList, fetchMessages]
  );

  useEffect(() => {
    if (!id) {
      return;
    }

    applyCswFromChatList(id);
  }, [id, chatList, applyCswFromChatList]);

  useEffect(() => {
    if (!id || !openContactCsw) {
      return;
    }

    if (String(openContactCsw.contactId) !== String(id)) {
      return;
    }

    setIsOldChat(Boolean(openContactCsw.is_expired));
    setCswExpiresAt(openContactCsw.csw_expires_at ?? null);
  }, [id, openContactCsw]);

  const fetchKeywords = useCallback(async () => {
    try {
      const response = await apiClient.get(`${api}chatbots/keywords`);
      setKeywords(response.data?.keywords);
    } catch (error) {
      console.error("Error fetching keywords:", error);
    }
  }, [api]);

  useEffect(() => {
    fetchKeywords();
  }, [fetchKeywords]);

  // On mobile: reset window scroll to 0 when the virtual keyboard is dismissed.
  // When the keyboard opens, the browser shrinks the visual viewport and may scroll
  // the page body up. When the keyboard closes the viewport grows back but the
  // scroll position is not restored automatically, leaving the chat offset.
  useEffect(() => {
    if (!isMobile || !window.visualViewport) return;
    let lastHeight = window.visualViewport.height;
    const handleViewportResize = () => {
      const newHeight = window.visualViewport.height;
      // Keyboard closed — viewport height grew
      if (newHeight > lastHeight) {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
      lastHeight = newHeight;
    };
    window.visualViewport.addEventListener('resize', handleViewportResize);
    return () => window.visualViewport.removeEventListener('resize', handleViewportResize);
  }, [isMobile]);

  useEffect(() => {
    if (!id) {
      return undefined;
    }

    setActiveContact(String(id));
    getChats(id);
  }, [id, getChats, setActiveContact]);

  useEffect(() => {
    clearSelectedMedia({ discardDevice: true });
  }, [id, clearSelectedMedia]);

  const handleValuesChange = useCallback((changedValues) => {
    const hasText = Boolean(changedValues.newMsg?.trim());
    updateSendDisabled(selectedMedia, hasText);
  }, [selectedMedia, updateSendDisabled]);

  return (
    <div className={`chat-content${isMobile ? ' chat-content-mobile' : ''}`}>
      <ChatContentHeader
        isSupportAgent={isSupportAgent}
        cswExpiresAt={cswExpiresAt}
        isExpired={isOldChat}
        name={info?.name}
        authToken={authToken}
        number={info.number}
        loading={loadingMessages}
        displayPhoneNumber={parentNumber}
        onRefresh={() => getChats(id)}
        api={api}
        getChats={getChats}
        activeAgent={activeAgent}
        wa_id={id}
        onAssignSuccess={handleAssignSuccess}
        messageSearch={messageSearch}
      />
      <ChatContentBody
        ref={chatBodyRef}
        setLoading={setLoadingMessages}
        messages={msgList}
        loading={loadingMessages}
        keywords={keywords}
        contactId={id}
        contactName={info?.name}
        number={info.number}
        onLoadMore={(newMessages) => updateMessagesForContact(id, () => newMessages)}
        onUpdateMessage={(messageId, patch) =>
          updateMessagesForContact(id, (prev) =>
            prev.map((item) =>
              item.id === messageId ? { ...item, ...patch } : item
            )
          )
        }
        getChats={getChats}
        activeSearchMatchId={messageSearch.activeMatch?.id || null}
        searchHighlightQuery={messageSearch.debouncedQuery}
        onReply={handleReply}
        parentNumber={parentNumber}
        onRegisterSetHasMore={(fn) => {
          setHasMoreRef.current = fn;
        }}
      />
      <ChatContentFooter
        form={form}
        menuState={menuState}
        number={info.number}
        contactName={info?.name}
        replyingTo={replyingToPreview}
        onCancelReply={clearReply}
        setDisable={setDisable}
        onSend={onSend}
        isOldChat={isOldChat}
        disable={disable}
        setLoading={setLoadingMessages}
        selectedMedia={selectedMedia}
        addSelectedMedia={addSelectedMedia}
        beginDeviceMediaUploads={beginDeviceMediaUploads}
        completeDeviceMediaUpload={completeDeviceMediaUpload}
        failDeviceMediaUpload={failDeviceMediaUpload}
        removeSelectedMedia={removeSelectedMedia}
        msgList={msgList}
        lastReportID={lastReportID}
        handleValuesChange={handleValuesChange}
        onSendLocation={sendLocationMessage}
        parentNumber={parentNumber}
      />
    </div>
  );
};

export default Conversation;
